import { validOverrideCents } from './overridePriceModel'
import { useEffect, useState, useRef, KeyboardEvent } from 'react'
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Modal, Space, Typography, Flex, InputNumber, Divider } from "antd"
import VSpace from "../../../../common/VSpace"
import { gold } from "@ant-design/colors"
import { centsToDollarsDisplay } from "../../../../displayFormatters"

const { Text } = Typography

type Props = {
  isOpen: boolean
  onClose: () => void
  onSave: (newPriceInCents: number) => void
  currentPrice: number
  min?: number
  additionalInfo: string | null
  isPerVisit?: boolean
  recoveryIncluded?: boolean
}

const OverridePriceModal = ({ isOpen, onClose, onSave, currentPrice, min, additionalInfo, isPerVisit, recoveryIncluded }: Props) => {
  const initialAmount = currentPrice / 100
  const [inputValue, setInputValue] = useState<number | null>(initialAmount)
  const inputRef = useRef<any>(null)

  useEffect(() => {
    setInputValue(currentPrice / 100)
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen, currentPrice])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '+' || e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      handleOk()
    }
    if (e.key === 'Escape') {
      onClose()
    }
  }

  const newCents = validOverrideCents(inputValue, min)
  const handleOk = () => {
    if (newCents !== undefined) {
      onSave(newCents)
    }
  }

  const priceText = isPerVisit ? 'Price Per Visit' : 'Price'

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={faInfoCircle} color={gold.primary} />
          <Text strong>Override {priceText}</Text>
        </Space>
      }
      open={isOpen}
      onOk={handleOk}
      onCancel={onClose}
      okText="Save"
      okButtonProps={{ disabled: newCents === undefined }}
      cancelText="Cancel"
    >
      <VSpace>
        {recoveryIncluded && <Text>The amount you enter is the customer’s card-inclusive total. The recovery allowance is included in this amount.</Text>}
        <Flex wrap="wrap" gap={4}>
          <Space>
            <Text>Dollar Amount: </Text>
            <InputNumber
              style={{ width: '100%' }}
              value={inputValue}
              type='number'
              onChange={setInputValue}
              onKeyDown={onKeyDown}
              addonBefore={"$"}
              precision={2}
              status={newCents === undefined ? 'error' : undefined}
              aria-label="Override dollar amount"
              ref={inputRef}
            />
          </Space>
          {additionalInfo && <Text type="secondary">
            {additionalInfo}
          </Text>}
        </Flex>
        <Flex vertical style={{ padding: 20, borderRadius: 8, border: '1px solid #d9d9d9' }}>
          <Flex justify="space-between">
            <Text>Current {priceText}</Text>
            <Text>{centsToDollarsDisplay(currentPrice)}</Text>
          </Flex>
          {
            inputValue !== null && inputValue !== initialAmount && <>
              <Divider />
              <Flex justify="space-between" align="start">
                <Flex vertical>
                  <Text strong style={{ fontSize: 20 }}>Override {priceText}</Text>
                  <Space>
                    <Text type="secondary" delete>{centsToDollarsDisplay(initialAmount * 100)}</Text>
                    <Text type="secondary">{centsToDollarsDisplay(inputValue * 100)}</Text>
                  </Space>
                </Flex>
                <Text strong style={{ fontSize: 20 }}>{centsToDollarsDisplay(inputValue * 100)}</Text>
              </Flex>
            </>
          }
        </Flex>
      </VSpace>
    </Modal>
  )
}

export default OverridePriceModal
